import { ReactNode } from "react";
import { PoolContributionCap, PoolFulfillment } from "../../types/program.response";
import Check from "../../components/icons/Check";
import VerificationWarning from "../../components/shared/VerificationWarning";

interface OverallProgressProps {
    completedCoursesCount: number;
    requiredCoursesCount: number;
    poolResults: PoolFulfillment[];
    /** 传入后 pool 进度行可点击(打开 pool 详情面板);一个池多条 constraint 行指向同一 poolKey */
    onPoolClick?: (poolKey: string) => void;
}

/** 行的语义:default=进行中(蓝),done=已满足(绿) */
type RowTone = "default" | "done";

/**
 * program 的"总体进度"区块。把课程完成度与 pool(课程池)判定统一到一套进度行里:
 * - courses / pool constraints:下限型目标,用进度条(所有 program 都有 courses 行)。
 * - pool contributionCaps:计入上限是规则不是目标,进度条会误导成"要填满",
 *   故渲染为进度条组下方的一句话说明;未用到时只陈述规则,到达上限转琥珀色提醒。
 * 契约见 docs/pool-frontend-migration-guide.md。
 */
export default function OverallProgress({ completedCoursesCount, requiredCoursesCount, poolResults, onPoolClick }: OverallProgressProps) {
    const hasPoolConstraints = poolResults.some((pool) => pool.constraints.length > 0);
    const hasCaps = poolResults.some((pool) => pool.contributionCaps.length > 0);

    return (
        <div className="border-b border-gray-200 py-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Overall Progress</h2>
            <div className="mt-2.5 flex flex-col gap-3.5">
                {!hasPoolConstraints && (
                    <ProgressRow
                        label="Courses"
                        applied={completedCoursesCount}
                        required={requiredCoursesCount}
                        tone={completedCoursesCount >= requiredCoursesCount && requiredCoursesCount > 0 ? "done" : "default"}
                    />
                )}
                {poolResults.flatMap((pool) =>
                    pool.constraints.map((c, i) => {
                        const row = (
                            <ProgressRow
                                label={c.label ?? "Requirement"}
                                applied={c.appliedUnitsCount}
                                required={c.requiredUnitsCount}
                                tone={c.satisfied ? "done" : "default"}
                                labelAccessory={c.needVerification ? <VerificationWarning iconClassName="w-3.5 h-3.5" /> : undefined}
                            />
                        );
                        return onPoolClick ? (
                            <button
                                key={`${pool.poolKey}-c-${i}`}
                                onClick={() => onPoolClick(pool.poolKey)}
                                className="block w-full text-left -mx-1.5 -my-1 px-1.5 py-1 rounded-md hover:bg-gray-100 transition-colors"
                            >
                                {row}
                            </button>
                        ) : (
                            <div key={`${pool.poolKey}-c-${i}`}>{row}</div>
                        );
                    })
                )}
            </div>
            {hasCaps && (
                <div className="mt-3.5 flex flex-col gap-1">
                    {poolResults.flatMap((pool) =>
                        pool.contributionCaps.map((cap, i) => (
                            <CapNote key={`${pool.poolKey}-cap-${i}`} cap={cap} />
                        ))
                    )}
                </div>
            )}
        </div>
    );
}


export function ProgressRow({ label, applied, required, tone, labelAccessory }: {
    label: string;
    applied: number;
    required: number;
    tone: RowTone;
    /** label 右侧的通用插槽(如需人工确认提示);本行不解读其语义,由调用方决定 */
    labelAccessory?: ReactNode;
}) {
    const percentage = required > 0
        ? Math.min(100, Math.round((applied / required) * 100))
        : (applied > 0 ? 100 : 0);
    const barColor = tone === "done" ? "bg-green-500" : "bg-blue-500";
    const labelColor = tone === "done" ? "text-gray-400" : "text-gray-700";

    return (
        <div>
            <div className="flex items-baseline justify-between gap-2">
                <span className="flex items-center gap-1 min-w-0">
                    <span className={`text-xs font-medium truncate ${labelColor}`}>{label}</span>
                    {labelAccessory}
                </span>
                <span className="text-xs font-medium text-gray-400 flex-shrink-0">{applied} / {required}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200">
                <div
                    className={`h-full rounded-full transition-[width] duration-300 ease-out ${barColor}`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
}

/**
 * Pool 详情页顶部的进度行:h1 已写池名,故单条 constraint 时不再重复 label。
 * 用一行状态(✓ Complete 绿 / In progress 蓝)+ 右侧计数领起,下接稍饱满的圆润进度条,
 * 完成态给出明确达成感,而非一条间延的裸绿条。多条 constraint 时各自带 label 区分。
 */
export function PoolProgressRow({ label, applied, required, satisfied, needVerification, showLabel }: {
    label: string;
    applied: number;
    required: number;
    satisfied: boolean;
    /** 后端判定口径存疑:满足态不给"纯绿一口咬定",补一个"需人工确认"提示 */
    needVerification: boolean;
    showLabel: boolean;
}) {
    const percentage = required > 0
        ? Math.min(100, Math.round((applied / required) * 100))
        : (applied > 0 ? 100 : 0);
    const barColor = satisfied ? "bg-green-500" : "bg-blue-500";

    return (
        <div>
            {showLabel && (
                <div className="mb-1.5 text-sm font-medium text-gray-600">{label}</div>
            )}
            <div className="mb-2 flex items-baseline justify-between gap-3">
                <span className="inline-flex items-center gap-1.5">
                    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${satisfied ? "text-green-600" : "text-blue-600"}`}>
                        {satisfied && <Check width={14} height={14} color="currentColor" className="translate-y-px" />}
                        {satisfied ? "Complete" : "In progress"}
                    </span>
                    {needVerification && <VerificationWarning />}
                </span>
                <span className="text-sm font-semibold tabular-nums text-gray-900">
                    {applied}
                    <span className="font-normal text-gray-400"> / {required}</span>
                </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                    className={`h-full rounded-full transition-[width] duration-500 ease-out ${barColor}`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
}

/** 计入上限的一句话说明:未使用时只陈述规则,有用量时展示用量,达到上限转琥珀色提醒超出部分不计 */
export function CapNote({ cap }: { cap: PoolContributionCap }) {
    const label = cap.label ?? "These courses";
    const unitSingular = cap.measure === "CREDIT" ? "credit" : "course";
    const unit = cap.n === 1 ? unitSingular : `${unitSingular}s`;
    const atCap = cap.appliedUnitsCount >= cap.n;

    const message = atCap
        ? `${label} — ${cap.n}-${unitSingular} cap reached; extra ${unitSingular}s won't count.`
        : cap.appliedUnitsCount > 0
            ? `${label} — ${cap.appliedUnitsCount} of ${cap.n} max ${unit} used.`
            : `${label} — at most ${cap.n} ${unit} count.`;

    return (
        <p className={`text-xs leading-snug ${atCap ? "text-amber-600" : "text-gray-500"}`}>
            {message}
        </p>
    );
}
